import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  IconButton
} from '@mui/material';
import { 
  Save, 
  Cancel, 
  CloudUpload, 
  Delete,
  Image as ImageIcon 
} from '@mui/icons-material';
import { Section, ImagemSection } from '../../types/festejo';

interface ImagemSectionEditorProps {
  section: Section | null;
  onSave: (section: Section) => void;
  onCancel: () => void;
}

interface FormData {
  title: string;
  imageUrl: string;
  description: string;
}

interface FormErrors {
  title?: string;
  imageUrl?: string;
}

const ImagemSectionEditor: React.FC<ImagemSectionEditorProps> = ({
  section,
  onSave,
  onCancel
}) => {
  const [formData, setFormData] = useState<FormData>({
    title: '',
    imageUrl: '',
    description: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [uploading, setUploading] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Carregar dados da seção se estiver editando
  useEffect(() => {
    if (section && section.type === 'imagem') {
      const imagemSection = section as ImagemSection;
      setFormData({
        title: imagemSection.title,
        imageUrl: imagemSection.imageUrl,
        description: imagemSection.description || ''
      });
      setImagePreview(imagemSection.imageUrl);
    }
  }, [section]);

  // Validar formulário
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Título deve ter pelo menos 3 caracteres';
    }

    if (!formData.imageUrl.trim()) {
      newErrors.imageUrl = 'Imagem é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manipular mudanças nos campos
  const handleFieldChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpar erro do campo
    //if (errors[field]) {
    //  setErrors(prev => ({ ...prev, [field]: undefined }));
   // }

    // Atualizar preview se for URL da imagem
    if (field === 'imageUrl') {
      setImagePreview(value);
    }
  };

  // Simular upload de arquivo (em um projeto real, isso faria upload para o servidor)
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem.');
      return;
    }

    // Validar tamanho (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Arquivo muito grande. Máximo 5MB.');
      return;
    }

    setUploading(true);

    // Simular upload com FileReader para preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      
      // Em um projeto real, aqui você faria o upload para o servidor
      // e receberia a URL final da imagem
      setTimeout(() => {
        const mockUrl = result; // Em produção seria a URL do servidor
        setFormData(prev => ({ ...prev, imageUrl: mockUrl }));
        setImagePreview(mockUrl);
        setUploading(false);
        
        // Limpar erro se existir
        if (errors.imageUrl) {
          setErrors(prev => ({ ...prev, imageUrl: undefined }));
        }
      }, 1500);
    };
    
    reader.readAsDataURL(file);
  };

  // Remover imagem
  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, imageUrl: '' }));
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Salvar seção
  const handleSave = () => {
    if (!validateForm()) return;

    const imagemSection: ImagemSection = {
      id: section?.id || '',
      type: 'imagem',
      title: formData.title.trim(),
      imageUrl: formData.imageUrl,
      description: formData.description.trim() || undefined,
      order: section?.order || 0
    };

    onSave(imagemSection);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Use seções de imagem para destacar fotos importantes como a imagem do padroeiro, 
        da igreja, ou momentos especiais do festejo.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Título da Seção"
          value={formData.title}
          onChange={handleFieldChange('title')}
          error={!!errors.title}
          helperText={errors.title || 'Ex: Imagem de São João Batista'}
          required
          sx={{ mb: 2 }}
        />

        {/* Upload de imagem */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Imagem *
          </Typography>
          
          {!imagePreview ? (
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                border: '2px dashed #ccc',
                backgroundColor: '#fafafa',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <Box>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Enviando imagem...
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <CloudUpload sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    Clique para selecionar uma imagem
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Formatos aceitos: JPG, PNG, WebP (máx. 5MB)
                  </Typography>
                </Box>
              )}
            </Paper>
          ) : (
            <Paper sx={{ p: 2, position: 'relative' }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 300,
                    borderRadius: 8,
                    display: 'block'
                  }}
                  onError={() => {
                    setImagePreview('');
                    setFormData(prev => ({ ...prev, imageUrl: '' }));
                  }}
                />
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.9)'
                    }
                  }}
                  onClick={handleRemoveImage}
                  size="small"
                >
                  <Delete />
                </IconButton>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  Trocar Imagem
                </Button>
              </Box>
            </Paper>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />

          {errors.imageUrl && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {errors.imageUrl}
            </Typography>
          )}
        </Box>

        {/* URL alternativa */}
        <TextField
          fullWidth
          label="Ou cole a URL da imagem"
          value={formData.imageUrl}
          onChange={handleFieldChange('imageUrl')}
          helperText="Você pode colar a URL de uma imagem da internet"
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Descrição (opcional)"
          value={formData.description}
          onChange={handleFieldChange('description')}
          helperText="Descrição que aparecerá abaixo da imagem"
        />
      </Box>

      {/* Preview */}
      {formData.title && imagePreview && (
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Preview:
          </Typography>
          <Typography variant="h6" gutterBottom>
            {formData.title}
          </Typography>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <img
              src={imagePreview}
              alt={formData.title}
              style={{
                maxWidth: '100%',
                maxHeight: 200,
                borderRadius: 8
              }}
            />
          </Box>
          {formData.description && (
            <Typography variant="body2" color="text.secondary" align="center">
              {formData.description}
            </Typography>
          )}
        </Box>
      )}

      {/* Dicas */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>💡 Dicas para imagens:</strong><br/>
          • Use imagens de alta qualidade e bem iluminadas<br/>
          • Prefira fotos em orientação paisagem para melhor visualização<br/>
          • Certifique-se de ter direitos de uso da imagem<br/>
          • Adicione uma descrição para tornar o conteúdo mais acessível
        </Typography>
      </Alert>

      {/* Botões */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<Cancel />}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={<Save />}
          disabled={uploading}
        >
          Salvar Seção
        </Button>
      </Box>
    </Box>
  );
};

export default ImagemSectionEditor;

